import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Card } from '../ui';
import type { Task } from '../../lib/types';

const tone = (s: Task['status']) =>
  (s === 'approved' || s === 'done'
    ? 'green'
    : s === 'blocked'
      ? 'amber'
      : s === 'in_review'
        ? 'blue'
        : 'slate') as 'green' | 'amber' | 'blue' | 'slate';

export function WorkflowTab({ tasks }: { tasks: Task[] }) {
  const { t } = useTranslation();
  const [all, setAll] = useState(false);
  const rows = all ? tasks : tasks.filter((x) => x.actionable);
  return (
    <Card
      title={t('tasks.title')}
      actions={
        <Button variant="secondary" onClick={() => setAll((v) => !v)}>
          {all ? t('tasks.onlyActionable') : t('tasks.showAll')}
        </Button>
      }
    >
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase text-slate-500">
          <tr>
            <th className="py-2">{t('tasks.key')}</th>
            <th>{t('tasks.task')}</th>
            <th>{t('tasks.owner')}</th>
            <th>{t('tasks.gate')}</th>
            <th>{t('tasks.planned')}</th>
            <th>{t('tasks.status')}</th>
            <th>{t('tasks.docsCol')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((x) => (
            <tr key={x.key} className={x.actionable ? '' : 'text-slate-400'}>
              <td className="py-2 font-mono text-xs">{x.key}</td>
              <td>
                {x.title}
                {!x.actionable && <span className="ml-2 text-xs">({t('tasks.readonly')})</span>}
                {x.predecessors.length > 0 && (
                  <div className="text-xs text-slate-400">← {x.predecessors.join(', ')}</div>
                )}
              </td>
              <td>
                {x.ownerRole ? (
                  <Badge tone="blue">{t(`roles.${x.ownerRole}`)}</Badge>
                ) : (
                  <span className="text-xs">{x.owner}</span>
                )}
              </td>
              <td className="text-xs">{x.gate}</td>
              <td className="text-xs">
                {x.plannedStart} → {x.plannedEnd}
              </td>
              <td>
                <Badge tone={tone(x.status)}>{t(`tasks.status_${x.status}`)}</Badge>
              </td>
              <td className="font-mono text-xs">{x.documentIds.join(', ') || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}
